import React from 'react';
import { Link } from 'react-router';

import { SnailGlyph } from '@/components/ui/SnailGlyph';

interface LegalLayoutProps {
    title: string;
    effectiveDate: string;
    /** The other legal doc, cross-linked in the header. */
    counterpart: { label: string; to: string };
    children: React.ReactNode;
}

/** Shared shell for the /terms and /privacy documents. */
const LegalLayout: React.FC<LegalLayoutProps> = ({
    title,
    effectiveDate,
    counterpart,
    children,
}) => (
    <div className="min-h-screen bg-background text-foreground">
        <header className="border-b border-border">
            <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
                <Link
                    to="/"
                    className="flex items-center gap-2 text-sm font-semibold text-foreground transition-opacity hover:opacity-80"
                >
                    <SnailGlyph size={20} className="text-primary" />
                    Snappy Trade
                </Link>
                <Link
                    to={counterpart.to}
                    className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                    {counterpart.label}
                </Link>
            </div>
        </header>

        <main className="mx-auto max-w-3xl px-5 py-10">
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            <p className="mt-2 text-xs text-muted-foreground">
                Effective date: {effectiveDate}
            </p>
            <div className="mt-8 flex flex-col gap-8">{children}</div>
        </main>

        <footer className="border-t border-border">
            <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-6 text-xs text-muted-foreground">
                <span>Snappy Trade</span>
                <Link
                    to="/"
                    className="transition-colors hover:text-foreground"
                >
                    Back to the app
                </Link>
            </div>
        </footer>
    </div>
);

interface LegalSectionProps {
    title: string;
    children: React.ReactNode;
}

/** One numbered/topical section of a legal document. */
export const LegalSection: React.FC<LegalSectionProps> = ({
    title,
    children,
}) => (
    <section>
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
            {title}
        </h2>
        <div className="mt-2 flex flex-col gap-3 text-sm leading-relaxed text-muted-foreground [&_li]:ml-5 [&_li]:list-disc [&_strong]:text-foreground/90">
            {children}
        </div>
    </section>
);

export default LegalLayout;
